/**
 * Conditional logic for form-builder forms — kept in sync with the CMS plugin's
 * `shared/types/conditions.ts` and `shared/types/flow.ts` (separate repo, so
 * duplicated here the same way TEXT_FORMATS is). Two things branch:
 *
 *  - a block is only rendered/validated while its `visibleWhen` passes;
 *  - a page's outgoing paths are tried in order and the first whose condition
 *    passes is the next page, so an unconditional path is the "else".
 *
 * The server re-runs the identical logic against the submitted answers, so
 * anything hidden or on a branch not taken is never enforced.
 */

export type ConditionOperator =
    | 'eq'
    | 'neq'
    | 'in'
    | 'nin'
    | 'contains'
    | 'ncontains'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'notEmpty'
    | 'empty'
    | 'truthy'
    | 'falsy';

export type ConditionValue = string | number | boolean | Array<string | number>;

export interface ConditionRule {
    fieldKey: string;
    operator: ConditionOperator;
    value?: ConditionValue;
}

export interface ConditionGroup {
    match: 'all' | 'any';
    rules: ConditionRule[];
}

/** A bare rule is tolerated on read (older forms) and normalized to a group. */
export type Condition = ConditionGroup | ConditionRule;

export interface FlowTransition {
    target: string;
    condition?: Condition;
    label?: string;
}

type Values = Record<string, unknown>;

/**
 * Structural minimums, so this module owns the condition types outright and the
 * render-schema types can import from it (rather than the other way around).
 */
interface BlockLike {
    props?: Record<string, unknown>;
}
interface PageLike {
    id: string;
    next?: FlowTransition[];
}

function normalizeCondition(condition: Condition | null | undefined): ConditionGroup | null {
    if (!condition) return null;
    if ('rules' in condition) {
        const rules = (condition.rules ?? []).filter((r) => r && r.fieldKey);
        return rules.length > 0 ? { match: condition.match === 'any' ? 'any' : 'all', rules } : null;
    }
    return condition.fieldKey ? { match: 'all', rules: [condition] } : null;
}

function isEmptyValue(value: unknown): boolean {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.values(value as Values).every((v) => v === undefined || v === null || v === '');
    return false;
}

function looseEquals(answer: unknown, expected: unknown): boolean {
    if (Array.isArray(answer)) return answer.some((a) => looseEquals(a, expected));
    if (typeof answer === 'boolean' || typeof expected === 'boolean') {
        const toBool = (v: unknown) => (typeof v === 'boolean' ? v : v === 'true' || v === 'yes' || v === 1 || v === '1');
        return toBool(answer) === toBool(expected);
    }
    if (isEmptyValue(answer) || isEmptyValue(expected)) return isEmptyValue(answer) && isEmptyValue(expected);
    return String(answer).trim().toLowerCase() === String(expected).trim().toLowerCase();
}

function asList(value: ConditionValue | undefined): Array<string | number> {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function compareOrdered(answer: unknown, expected: ConditionValue | undefined, cmp: (a: number, b: number) => boolean): boolean {
    const num = (v: unknown) => (typeof v === 'number' ? v : Number(String(v ?? '').trim()));
    const a = num(answer);
    const b = num(expected);
    if (!Number.isNaN(a) && !Number.isNaN(b)) return cmp(a, b);
    const da = Date.parse(String(answer ?? ''));
    const db = Date.parse(String(expected ?? ''));
    if (Number.isNaN(da) || Number.isNaN(db)) return false;
    return cmp(da, db);
}

function includesValue(answer: unknown, expected: ConditionValue | undefined): boolean {
    if (Array.isArray(answer)) return answer.some((a) => looseEquals(a, expected));
    if (isEmptyValue(answer)) return false;
    return String(answer).toLowerCase().includes(String(expected ?? '').toLowerCase());
}

function evaluateRule(rule: ConditionRule, data: Values): boolean {
    const answer = data?.[rule.fieldKey];
    switch (rule.operator) {
        case 'eq':
            return looseEquals(answer, rule.value);
        case 'neq':
            return !looseEquals(answer, rule.value);
        case 'in':
            return asList(rule.value).some((v) => looseEquals(answer, v));
        case 'nin':
            return !asList(rule.value).some((v) => looseEquals(answer, v));
        case 'contains':
            return includesValue(answer, rule.value);
        case 'ncontains':
            return !includesValue(answer, rule.value);
        case 'gt':
            return compareOrdered(answer, rule.value, (a, b) => a > b);
        case 'gte':
            return compareOrdered(answer, rule.value, (a, b) => a >= b);
        case 'lt':
            return compareOrdered(answer, rule.value, (a, b) => a < b);
        case 'lte':
            return compareOrdered(answer, rule.value, (a, b) => a <= b);
        case 'notEmpty':
        case 'truthy':
            return !isEmptyValue(answer);
        case 'empty':
        case 'falsy':
            return isEmptyValue(answer);
        default:
            return true;
    }
}

/** No condition = always true, so an unconditional path is the fallback. */
export function evaluateCondition(condition: Condition | null | undefined, data: Values): boolean {
    const group = normalizeCondition(condition);
    if (!group) return true;
    return group.match === 'any' ? group.rules.some((r) => evaluateRule(r, data)) : group.rules.every((r) => evaluateRule(r, data));
}

/** A block's visibility condition (`conditional` is the legacy prop name). */
export function blockCondition(block: BlockLike | undefined | null): Condition | undefined {
    const props = (block?.props ?? {}) as Values;
    return (props.visibleWhen as Condition | undefined) ?? (props.conditional as Condition | undefined);
}

export function isBlockVisible(block: BlockLike, data: Values): boolean {
    return evaluateCondition(blockCondition(block), data);
}

export function visibleBlocks<T extends BlockLike>(blocks: T[] | undefined | null, data: Values): T[] {
    return (blocks ?? []).filter((b) => isBlockVisible(b, data));
}

/** Conditional paths first (author order), unconditional fallbacks last. */
function orderedTransitions(next: FlowTransition[] | undefined | null): FlowTransition[] {
    const list = (next ?? []).filter((t) => t && t.target);
    return [...list.filter((t) => t.condition), ...list.filter((t) => !t.condition)];
}

/** Id of the page after `page` given the answers, or null when the form ends. */
export function resolveNextPageId(page: PageLike | undefined | null, data: Values): string | null {
    if (!page) return null;
    const match = orderedTransitions(page.next).find((t) => evaluateCondition(t.condition, data));
    return match ? match.target : null;
}

/**
 * The pages this set of answers runs through, start to finish. Used both for
 * the "Step X of N" projection and to scope validation to the branch actually
 * taken. Stops on a revisit so a looping graph still terminates.
 */
export function resolvePagePath<T extends PageLike>(pages: T[], startId: string | undefined, data: Values): T[] {
    if (!pages?.length) return [];
    const byId = new Map(pages.map((p) => [p.id, p]));
    const path: T[] = [];
    const seen = new Set<string>();
    let current: T | undefined = (startId ? byId.get(startId) : undefined) ?? pages[0];
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        path.push(current);
        const nextId = resolveNextPageId(current, data);
        current = nextId ? byId.get(nextId) : undefined;
    }
    return path;
}
